import { useRequest } from 'ahooks';
import CaregiverAvatar from 'avatar/CaregiverAvatar';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Skeleton } from 'components/ui/skeleton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiRegisteredCaregiversManager from 'services/api/ApiRegisteredCaregiversManager';

export type CaregiverAvatarListProps = {
  direction?: 'horizontal' | 'vertical';
  autoScroll?: boolean;
};

const CaregiverAvatarList: React.FC<CaregiverAvatarListProps> = ({ direction, autoScroll }) => {
  // Hooks
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const [selectedCaregiverId, setSelectedCaregiverId] = useState<string | null>(null);

  const { loading, data } = useRequest(() => ApiRegisteredCaregiversManager.getRegisteredCaregiversList(), {
    cacheKey: 'registered-caregivers.list',
  });

  const shuffledData = useMemo(() => {
    return data?.sort(() => Math.random() - 0.5);
  }, [data]);

  const initialLoading = data === undefined && loading;

  const caregiversByQualification = useMemo(() => {
    if (!shuffledData) return { medical: [], paramedical: [], alternative_medicine: [] };

    return shuffledData.reduce(
      (acc, caregiver) => {
        // Get the first group's qualification level (or default to paramedical)
        const qualificationLevel =
          caregiver.caregiver.caregiver_groups.length > 0
            ? caregiver.caregiver.caregiver_groups[0].qualification_level || 'paramedical'
            : 'paramedical';

        if (qualificationLevel === 'medical') {
          acc.medical.push(caregiver);
        } else if (qualificationLevel === 'paramedical') {
          acc.paramedical.push(caregiver);
        } else if (qualificationLevel === 'alternative_medicine') {
          acc.alternative_medicine.push(caregiver);
        }

        return acc;
      },
      { medical: [], paramedical: [], alternative_medicine: [] } as Record<string, typeof shuffledData>,
    );
  }, [shuffledData]);

  const clearAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scrollABit = useCallback(() => {
    if (containerRef.current && autoScroll) {
      containerRef.current.scrollTo({
        left: containerRef.current.scrollLeft + 1,
      });
    }
  }, [autoScroll]);

  const handleUserInteraction = useCallback(() => {
    clearAutoScroll();
  }, [clearAutoScroll]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      // Clear any existing intervals/timeouts
      clearAutoScroll();

      // Start the scrolling interval
      intervalRef.current = setInterval(scrollABit, 40);

      // Clear the interval after 10 seconds
      timeoutRef.current = setTimeout(() => {
        clearAutoScroll();
      }, 30000);
    }

    // Cleanup on unmount or when autoScroll changes
    return () => {
      clearAutoScroll();
    };
  }, [autoScroll, scrollABit, clearAutoScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !autoScroll) return;

    // Add event listeners for user interaction
    // Note: We don't listen to 'scroll' events because programmatic scrolling
    // would trigger them and stop the auto-scroll prematurely
    container.addEventListener('mousedown', handleUserInteraction);
    container.addEventListener('touchstart', handleUserInteraction);
    container.addEventListener('wheel', handleUserInteraction);

    return () => {
      container.removeEventListener('mousedown', handleUserInteraction);
      container.removeEventListener('touchstart', handleUserInteraction);
      container.removeEventListener('wheel', handleUserInteraction);
    };
  }, [autoScroll, handleUserInteraction]);

  return (
    <>
      {direction === 'horizontal' ? (
        <div
          className="flex h-auto w-full items-start gap-6 overflow-x-auto overflow-y-hidden pb-2 [scrollbar-width:none]"
          ref={containerRef}
        >
          {initialLoading && !data && (
            <div className="mt-4 flex w-full items-center justify-center gap-4">
              <Skeleton className="size-12 rounded-full" />
              <Skeleton className="size-12 rounded-full" />
              <Skeleton className="size-12 rounded-full" />
              <Skeleton className="size-12 rounded-full" />
            </div>
          )}
          {Object.entries(caregiversByQualification)
            .filter(([, caregivers]) => caregivers.length > 0)
            .map(([qualification, caregivers]) => (
              <div key={qualification} className="flex flex-col gap-1">
                <p className="my-0 text-left text-sm font-medium text-subtle-foreground">
                  {t(`caregiverQualification.${qualification}`)}
                </p>
                <div className="flex w-fit items-start gap-4 pr-4">
                  {caregivers.map((caregiver, index) => {
                    const firstGroupName =
                      caregiver.caregiver.caregiver_groups.length > 0
                        ? caregiver.caregiver.caregiver_groups[0].display_name
                        : '';
                    const initials = caregiver.caregiver.first_name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <div
                        key={`avatar-${index}`}
                        className="flex min-w-fit flex-[0_0_auto] cursor-pointer flex-row items-center gap-2"
                        onClick={() => setSelectedCaregiverId(caregiver.user.id)}
                      >
                        <Avatar size="lg" className="flex-[0_0_auto]">
                          <AvatarImage src={caregiver.user.photo_url} />
                          <AvatarFallback>{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex min-w-0 flex-col">
                          <p className="m-0 whitespace-nowrap text-left text-base text-foreground">
                            {caregiver.caregiver.first_name}
                          </p>
                          <p className="m-0 max-w-20 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-subtle-foreground">
                            {firstGroupName}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      ) : (
        // Vertical list with qualification grouping
        <div className="w-full">
          {initialLoading && (
            <div className="mt-4 flex w-full items-center justify-center gap-4">
              <Skeleton className="size-12 rounded-full" />
              <Skeleton className="size-12 rounded-full" />
              <Skeleton className="size-12 rounded-full" />
            </div>
          )}
          {Object.entries(caregiversByQualification)
            .filter(([, caregivers]) => caregivers.length > 0)
            .map(([qualification, caregivers]) => (
              <div key={qualification} className="mb-6">
                <h3 className="text-center text-subtle-foreground">{t(`caregiverQualification.${qualification}`)}</h3>
                <ul
                  className="m-0 grid list-none gap-1 p-0"
                  style={{ gridTemplateColumns: `repeat(${Math.min(4, caregivers.length)}, minmax(0, 1fr))` }}
                >
                  {caregivers.map((item, index) => {
                    const firstGroupName =
                      item.caregiver.caregiver_groups.length > 0 ? item.caregiver.caregiver_groups[0].display_name : '';

                    return (
                      <li
                        key={`avatar-${index}`}
                        className="cursor-pointer py-3"
                        onClick={() => setSelectedCaregiverId(item.user.id)}
                      >
                        <CaregiverAvatar
                          avatar={item.user.photo_url}
                          name={item.caregiver.first_name}
                          caption={firstGroupName}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
        </div>
      )}
      {selectedCaregiverId && (
        <CaregiverProfileModal caregiverId={selectedCaregiverId} open onClose={() => setSelectedCaregiverId(null)} />
      )}
    </>
  );
};
export default CaregiverAvatarList;
